import React, { useState } from 'react';
import {
  Box, Typography, Button, Chip, Stack, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Card, CardContent, Grid, Tooltip, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Send as SendIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp, TrendingDown, TrendingFlat,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAdminWeeklyReports,
  generateWeeklyReport,
  resendWeeklyReport,
  updateWeeklyReportStats,
  deleteWeeklyReport,
  WeeklyReport,
  WeeklyReportStats,
} from '../../services/api/weeklyReportApi';
import { formatDateTime } from '../../utils/formatters';

const statusLabels: Record<string, { label: string; color: 'default' | 'warning' | 'success' | 'error' | 'info' }> = {
  generating: { label: '생성 중', color: 'warning' },
  ready:      { label: '준비됨', color: 'info' },
  notifying:  { label: '발송 중', color: 'warning' },
  completed:  { label: '완료', color: 'success' },
  failed:     { label: '실패', color: 'error' },
};

function formatWeekRange(start: string, end: string) {
  const s = new Date(start);
  const month = s.getMonth() + 1;
  const firstDay = new Date(s.getFullYear(), s.getMonth(), 1);
  const firstDayOfWeek = firstDay.getDay();
  const weekNum = Math.ceil((s.getDate() + (firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1)) / 7);
  const e = new Date(end);
  return `${s.getFullYear()}년 ${month}월 ${weekNum}주차 (${s.getMonth()+1}/${s.getDate()} ~ ${e.getMonth()+1}/${e.getDate()})`;
}

function ChangeRateChip({ rate }: { rate: number }) {
  if (rate > 0) return <Chip icon={<TrendingUp />} label={`+${rate}%`} size="small" color="error" />;
  if (rate < 0) return <Chip icon={<TrendingDown />} label={`${rate}%`} size="small" color="success" />;
  return <Chip icon={<TrendingFlat />} label="0%" size="small" />;
}

interface DetailDialogProps {
  report: WeeklyReport | null;
  onClose: () => void;
}

const DetailDialog: React.FC<DetailDialogProps> = ({ report, onClose }) => {
  if (!report) return null;
  const s = report.stats;
  const p = report.prevWeekStats;
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        주간 리포트 상세 — {formatWeekRange(report.weekStartDate, report.weekEndDate)}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {report.summary}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'APK 스캔', cur: s.apkScanCount, prev: p.apkScanCount },
            { label: '악성앱 탐지', cur: s.malwareDetected, prev: p.malwareDetected },
            { label: 'SMS 스캔', cur: s.smsScanCount, prev: p.smsScanCount },
            { label: '피싱 SMS', cur: s.phishingSmsDetected, prev: p.phishingSmsDetected },
            { label: '보이스피싱 차단', cur: s.voicephishingBlocked, prev: p.voicephishingBlocked },
            { label: '스팸 차단', cur: s.spamBlocked, prev: p.spamBlocked },
          ].map(({ label, cur, prev }) => {
            const rate = prev === 0 ? (cur > 0 ? 100 : 0) : Math.round(((cur - prev) / prev) * 100);
            return (
              <Grid size={{ xs: 6, sm: 4 }} key={label}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="h6">{cur.toLocaleString()}건</Typography>
                    <ChangeRateChip rate={rate} />
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        {report.topThreats.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Top 위협</Typography>
            <Stack spacing={1}>
              {report.topThreats.map((t, i) => (
                <Box key={t.type} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 20 }}>#{i + 1}</Typography>
                  <Typography variant="body2" sx={{ flex: 1 }}>{t.label}</Typography>
                  <Typography variant="body2">{t.count.toLocaleString()}건</Typography>
                  <ChangeRateChip rate={t.changeRate} />
                  {t.topExample && (
                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      예: {t.topExample}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

const WeeklyReportPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [detailReport, setDetailReport] = useState<WeeklyReport | null>(null);
  const [generateDialog, setGenerateDialog] = useState(false);
  const [weekStartDate, setWeekStartDate] = useState('');
  const [editReport, setEditReport] = useState<WeeklyReport | null>(null);
  const [editStats, setEditStats] = useState<Partial<WeeklyReportStats>>({});
  const [editSummary, setEditSummary] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string | null }>({ open: false, id: null });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin-weekly-reports'],
    queryFn: getAdminWeeklyReports,
  });

  const generateMutation = useMutation({
    mutationFn: () => generateWeeklyReport(weekStartDate || undefined),
    onSuccess: (report) => {
      queryClient.invalidateQueries({ queryKey: ['admin-weekly-reports'] });
      setSnackbar({ open: true, message: report ? '리포트가 생성되었습니다.' : '이미 존재하는 주차입니다.', severity: 'success' });
      setGenerateDialog(false);
      setWeekStartDate('');
    },
    onError: (e: any) => setSnackbar({ open: true, message: e.message, severity: 'error' }),
  });

  const resendMutation = useMutation({
    mutationFn: resendWeeklyReport,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin-weekly-reports'] });
      setSnackbar({ open: true, message: `재발송 완료: 성공 ${result.notifiedCount}건, 실패 ${result.failedCount}건`, severity: 'success' });
    },
    onError: (e: any) => setSnackbar({ open: true, message: e.message, severity: 'error' }),
  });

  const editMutation = useMutation({
    mutationFn: ({ id, stats, summary }: { id: string; stats: Partial<WeeklyReportStats>; summary: string }) =>
      updateWeeklyReportStats(id, stats, summary),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-weekly-reports'] });
      setSnackbar({ open: true, message: '수치가 수정되었습니다.', severity: 'success' });
      setEditReport(null);
    },
    onError: (e: any) => setSnackbar({ open: true, message: e.message, severity: 'error' }),
  });

  const handleOpenEdit = (report: WeeklyReport) => {
    setEditReport(report);
    setEditStats({ ...report.stats });
    setEditSummary(report.summary || '');
  };

  const deleteMutation = useMutation({
    mutationFn: deleteWeeklyReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-weekly-reports'] });
      setSnackbar({ open: true, message: '리포트가 삭제되었습니다.', severity: 'success' });
      setDeleteConfirm({ open: false, id: null });
    },
    onError: (e: any) => setSnackbar({ open: true, message: e.message, severity: 'error' }),
  });

  const summaryStats = data?.stats;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">주간 보안 리포트</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-weekly-reports'] })}
          >
            새로고침
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setGenerateDialog(true)}>
            리포트 생성
          </Button>
        </Stack>
      </Box>

      {/* 요약 통계 카드 */}
      {summaryStats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: '전체 리포트', value: summaryStats.total },
            { label: '완료', value: summaryStats.completed },
            { label: '실패', value: summaryStats.failed },
            { label: '총 FCM 발송', value: summaryStats.totalNotified.toLocaleString() },
            { label: 'FCM 실패', value: summaryStats.totalFailed.toLocaleString() },
          ].map(({ label, value }) => (
            <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={label}>
              <Card>
                <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="h5">{value}</Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* 리포트 목록 테이블 */}
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>주차</TableCell>
              <TableCell align="center">상태</TableCell>
              <TableCell align="right">악성앱</TableCell>
              <TableCell align="right">피싱 SMS</TableCell>
              <TableCell align="right">보이스피싱</TableCell>
              <TableCell align="right">FCM 발송</TableCell>
              <TableCell align="right">공유</TableCell>
              <TableCell>생성일</TableCell>
              <TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">로딩 중...</TableCell>
              </TableRow>
            ) : (data?.reports || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  생성된 리포트가 없습니다.
                </TableCell>
              </TableRow>
            ) : (data?.reports || []).map((report) => {
              const st = statusLabels[report.status] ?? { label: report.status, color: 'default' };
              return (
                <TableRow key={report._id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => setDetailReport(report)}
                    >
                      {formatWeekRange(report.weekStartDate, report.weekEndDate)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={st.label} color={st.color} size="small" />
                  </TableCell>
                  <TableCell align="right">{report.stats?.malwareDetected ?? 0}</TableCell>
                  <TableCell align="right">{report.stats?.phishingSmsDetected ?? 0}</TableCell>
                  <TableCell align="right">{report.stats?.voicephishingBlocked ?? 0}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {report.notifiedCount ?? 0}
                      {(report.failedCount ?? 0) > 0 && (
                        <Typography component="span" variant="caption" color="error"> ({report.failedCount} 실패)</Typography>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{report.shareCount ?? 0}</TableCell>
                  <TableCell>
                    <Typography variant="caption">{formatDateTime(report.createdAt)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="수치 편집">
                        <IconButton size="small" color="default" onClick={() => handleOpenEdit(report)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="FCM 재발송">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={report.status !== 'completed' || resendMutation.isPending}
                            onClick={() => resendMutation.mutate(report._id)}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="삭제">
                        <IconButton size="small" color="error" onClick={() => setDeleteConfirm({ open: true, id: report._id })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* 리포트 생성 다이얼로그 */}
      <Dialog open={generateDialog} onClose={() => setGenerateDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>주간 리포트 생성</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            날짜를 비워두면 이번 주 리포트를 생성합니다.
          </Typography>
          <TextField
            fullWidth
            label="주 시작일 (선택)"
            type="date"
            value={weekStartDate}
            onChange={(e) => setWeekStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="KST 기준 월요일 날짜 입력"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setGenerateDialog(false); setWeekStartDate(''); }}>취소</Button>
          <Button variant="contained" onClick={() => generateMutation.mutate()} disabled={generateMutation.isPending}>
            생성
          </Button>
        </DialogActions>
      </Dialog>

      {/* 상세 다이얼로그 */}
      <DetailDialog report={detailReport} onClose={() => setDetailReport(null)} />

      {/* 수치 편집 다이얼로그 */}
      <Dialog open={!!editReport} onClose={() => setEditReport(null)} maxWidth="sm" fullWidth>
        <DialogTitle>수치 수동 편집 — {editReport ? formatWeekRange(editReport.weekStartDate, editReport.weekEndDate) : ''}</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            실제 집계 데이터를 덮어씁니다. 모바일 앱에 즉시 반영됩니다.
          </Typography>
          <Stack spacing={2}>
            {([
              { key: 'malwareDetected',      label: '악성앱 탐지' },
              { key: 'phishingSmsDetected',  label: '피싱 SMS 탐지' },
              { key: 'voicephishingBlocked', label: '보이스피싱 차단' },
              { key: 'spamBlocked',          label: '스팸 전화 차단' },
              { key: 'apkScanCount',         label: 'APK 스캔 수' },
              { key: 'smsScanCount',         label: 'SMS 분석 수' },
            ] as { key: keyof WeeklyReportStats; label: string }[]).map(({ key, label }) => (
              <TextField
                key={key}
                label={label}
                type="number"
                size="small"
                value={editStats[key] ?? 0}
                onChange={(e) => setEditStats(prev => ({ ...prev, [key]: Number(e.target.value) }))}
                inputProps={{ min: 0 }}
              />
            ))}
            <TextField
              label="요약 문구"
              multiline
              rows={3}
              value={editSummary}
              onChange={(e) => setEditSummary(e.target.value)}
              helperText="비워두면 자동 생성된 문구가 유지됩니다."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditReport(null)}>취소</Button>
          <Button
            variant="contained"
            disabled={editMutation.isPending}
            onClick={() => editReport && editMutation.mutate({ id: editReport._id, stats: editStats, summary: editSummary })}
          >
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null })} maxWidth="xs" fullWidth>
        <DialogTitle>리포트 삭제</DialogTitle>
        <DialogContent>
          <Typography>이 리포트를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, id: null })}>취소</Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            onClick={() => deleteConfirm.id && deleteMutation.mutate(deleteConfirm.id)}
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WeeklyReportPage;
